import React, {useEffect, useState} from 'react';
import {TagSmall} from '@wix/wix-ui-icons-common/on-stage';
import {SummaryDataHooks} from '../AsideSummary/AsideSummary';
import {useControllerProps} from '../../Widget/ControllerContext';
import {CodeInputSection} from '../CodeInputSection/CodeInputSection';
import {ILocaleKeys, useLocaleKeys} from '../../../../locale-keys/LocaleKeys';
import {CheckoutErrorCode} from '../../../../domain/utils/errors';
import {CheckoutErrorModel} from '../../../../domain/models/checkout/CheckoutError.model';
import {AddCouponErrorModel} from '../../../../domain/models/checkout/Errors/AddCouponError.model';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {SPECS} from '../../../../common/constants';

export enum CouponInputDataHooks {
  enterACodeButtonDisabledTooltip = 'CouponInput.enterACodeButtonDisabledTooltip',
}

export const CouponInput: React.FC<{mobilePosition?: string}> = ({mobilePosition}) => {
  const localeKeys = useLocaleKeys();
  const {experiments} = useExperiments();
  const {cart, cartSummary, applyCouponError, applyCoupon, removeCoupon, onAddCouponSectionOpen, isCouponUpdating} =
    useControllerProps().checkoutStore;

  const newCheckoutErrorModels = experiments.enabled(SPECS.NewCheckoutErrorModels);

  const [couponCodeError, setCouponCodeError] = useState(
    applyCouponError ? getErrorTextFromCouponError(applyCouponError, localeKeys, newCheckoutErrorModels) : undefined
  );

  useEffect(() => {
    const error = applyCouponError ?? cartSummary?.errors?.couponError;
    setCouponCodeError(error ? getErrorTextFromCouponError(error, localeKeys, newCheckoutErrorModels) : undefined);
  }, [cartSummary, applyCouponError, localeKeys, newCheckoutErrorModels]);

  const handleAddCouponSectionOpen = () => {
    onAddCouponSectionOpen(mobilePosition);
  };

  const handleApplyCode = (couponCode: string) => {
    void applyCoupon(couponCode, mobilePosition);
  };

  const handleRemoveCode = () => {
    void removeCoupon(mobilePosition);
  };

  const enterACodeButtonDisabledTooltipData = {
    dataHook: CouponInputDataHooks.enterACodeButtonDisabledTooltip,
    content: localeKeys.checkout.paymentDetails.applyCoupon.cantPurchaseGC.tooltip(),
  };

  return (
    <CodeInputSection
      dataHook={SummaryDataHooks.couponSectionInput}
      enterACodeButtonText={localeKeys.checkout.coupon.label()}
      onCodeInputOpen={handleAddCouponSectionOpen}
      applyCodeText={localeKeys.checkout.coupon.applyButton()}
      removeCodeText={localeKeys.checkout.coupon.removeButton()}
      codeValue={cart.coupon?.code}
      onApplyCode={handleApplyCode}
      onRemoveCode={handleRemoveCode}
      error={couponCodeError}
      icon={<TagSmall />}
      placeholder={localeKeys.checkout.paymentDetails.enterPromoCode.placeholder()}
      isUpdating={isCouponUpdating}
      isLoading={!cartSummary}
      isEnterACodeButtonDisabled={cart.hasGiftCardItems}
      enterACodeButtonDisabledTooltipData={enterACodeButtonDisabledTooltipData}
      ariaLabel={localeKeys.checkout.coupon.inputCode.ariaLabel()}
    />
  );
};

const getErrorTextFromCouponError = (
  couponError: CheckoutErrorModel | AddCouponErrorModel,
  localeKeys: ILocaleKeys,
  newCheckoutErrorModels: boolean
): string => {
  if (newCheckoutErrorModels) {
    return (couponError as AddCouponErrorModel).description;
  }
  switch ((couponError as CheckoutErrorModel)?.code) {
    case CheckoutErrorCode.ERROR_COUPON_IS_NOT_ACTIVE_YET:
      return localeKeys.checkout.coupon.notActiveYet.error();
    case CheckoutErrorCode.ERROR_COUPON_HAS_EXPIRED:
      return localeKeys.checkout.coupon.expired.error();
    case CheckoutErrorCode.ERROR_COUPON_USAGE_EXCEEDED:
      return localeKeys.checkout.coupon.maxUsage.error();
    case CheckoutErrorCode.ERROR_COUPON_IS_DISABLED:
      return localeKeys.checkout.coupon.disabled.error();
    case CheckoutErrorCode.ERROR_INVALID_COMMAND_FIELD:
      return localeKeys.checkout.coupon.characterLimit.error();
    case CheckoutErrorCode.ERROR_COUPON_LIMIT_PER_CUSTOMER_EXCEEDED:
      return localeKeys.checkout.coupon.alreadyUsed.error();
    case CheckoutErrorCode.ERROR_INVALID_SUBTOTAL:
      return localeKeys.checkout.coupon.invalidSubtotal.error({
        subtotal: (couponError as CheckoutErrorModel)?.data?.description,
      });
    case CheckoutErrorCode.ERROR_INVALID_PRODUCTS:
      return localeKeys.checkout.coupon.invalidProducts.error();
    case CheckoutErrorCode.ERROR_INVALID_PRODUCT_QUANTITY:
      return localeKeys.checkout.coupon.productQuantity.error({
        quantity: (couponError as CheckoutErrorModel)?.data?.description,
      });
    case CheckoutErrorCode.ERROR_NOT_APPLICABLE_FOR_SUBSCRIPTIONS:
      return localeKeys.checkout.coupon.subscriptions.error();
    case CheckoutErrorCode.ERROR_COUPON_DOES_NOT_EXIST:
    default:
      return localeKeys.checkout.coupon.invalidGeneral.error();
  }
};
